import React from 'react';

interface EmailBodyPreviewProps {
  html: string | null;
  height?: number;
}

/**
 * Email body preview component that properly overrides inline styles from email HTML.
 * 
 * Problem: Email HTML (from emailHtmlConverter.ts) contains inline font-size styles (e.g., font-size: 14px)
 * that cannot be overridden by wrapper CSS or Tailwind prose classes.
 * 
 * Solution: Use a scoped <style> tag with !important to force override inline styles.
 */
const EmailBodyPreview: React.FC<EmailBodyPreviewProps> = ({ html, height = 200 }) => {
  // Generate a unique scope class for this instance
  const scopeClass = 'email-preview-scope';
  
  // CSS overrides with !important to defeat inline styles
  // Body text gets base size, signature (inside table[width="500"] or border-top div) gets 45% size
  const overrideStyles = `
    /* Base styles for body text */
    .${scopeClass} p,
    .${scopeClass} td,
    .${scopeClass} div,
    .${scopeClass} span,
    .${scopeClass} a,
    .${scopeClass} li,
    .${scopeClass} th,
    .${scopeClass} h1,
    .${scopeClass} h2,
    .${scopeClass} h3,
    .${scopeClass} h4,
    .${scopeClass} h5,
    .${scopeClass} h6 {
      font-size: 16px !important;
      line-height: 1.5 !important;
    }
    
    /* Scale the entire signature container to 45% */
    /* This scales everything proportionally: text, images, spacing */
    .${scopeClass} > div[style*="border-top"] {
      transform: scale(0.65) !important;
      transform-origin: top left !important;
      margin-bottom: -55% !important; /* Collapse empty space from scaling */
    }
    
    /* Fallback: Scale signature table directly if border-top targeting fails */
    .${scopeClass} table[width="500"] {
      transform: scale(0.65) !important;
      transform-origin: top left !important;
    }
    
    .${scopeClass} img {
      max-width: 100% !important;
      height: auto !important;
    }
    
    .${scopeClass} table {
      max-width: 100% !important;
    }
  `;

  return (
    <div 
      className="overflow-hidden rounded border bg-background relative"
      style={{ height: `${height}px` }}
    >
      {/* Scoped style overrides */}
      <style>{overrideStyles}</style>
      
      {/* Email content with overflow scroll */}
      <div 
        className={`${scopeClass} p-3 overflow-auto h-full`}
        dangerouslySetInnerHTML={{ __html: html || '<em class="text-muted-foreground">No content</em>' }}
      />
      
      {/* Fade gradient at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background to-transparent pointer-events-none" />
    </div>
  );
};

export default EmailBodyPreview;
